import { ConfigField, ConfigSlideId } from '@/eventConfig/types.ts';
import React, { FC, useEffect, useRef, useState, useId } from 'react';
import ReactMarkdown from 'react-markdown';
import { translate } from '@/translation/translate.ts';
import { FormGetFieldStatusFn, FormOnChangeFn } from '@/form/FormProvider.tsx';

interface CollapisbleProps {
  field: ConfigField;
  slideId: ConfigSlideId;
  onChange: FormOnChangeFn;
  getFieldStatus: FormGetFieldStatusFn;
}

export const CollapsibleInput: FC<CollapisbleProps> = ({ field, onChange, slideId }) => {
  useEffect(() => {
    // Commit each Collapsible as being valid
    const value = null;
    const fieldValue = { raw: value, normalized: value };
    onChange(field.name, { value: fieldValue, slideId, isValid: true, hint: undefined });

    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  const validators = field.validator || [];
  const isRequired = validators.find((v) => v.type === 'required' || v.type === 'required_if');

  const detailsRef = useRef<HTMLDetailsElement>(null);
  const summaryRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const reactId = useId();
  const baseId = `collapsible-${String(field.name)}-${String(slideId ?? '')}-${reactId}`;
  const summaryId = `${baseId}-summary`;
  const contentId = `${baseId}-content`;

  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
  ];

  if (!field.content || !field.label) {
    return null;
  }

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type}>
        <details
          ref={detailsRef}
          className={isRequired ? 'required' : ''}
          onToggle={() => {
            const isOpen = Boolean(detailsRef.current?.open);
            setOpen(isOpen);
            if (isOpen) {
              summaryRef.current?.focus();
            }
          }}
        >
          <summary
            id={summaryId}
            ref={summaryRef}
            aria-expanded={open}
            aria-controls={contentId}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (detailsRef.current) {
                  detailsRef.current.open = !detailsRef.current.open;
                  setOpen(detailsRef.current.open);
                  if (detailsRef.current.open) {
                    summaryRef.current?.focus();
                  }
                }
              }
            }}
          >
            <ReactMarkdown unwrapDisallowed={true} allowedElements={['ol', 'ul', 'li', 'a', 'u', 'strong', 'br']}>
              {translate(field.label)}
            </ReactMarkdown>
          </summary>
          <div id={contentId} role="region" aria-labelledby={summaryId}>
            <ReactMarkdown
              unwrapDisallowed={true}
              allowedElements={['ol', 'ul', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'br', 'u']}
            >
              {translate(field.content)}
            </ReactMarkdown>
          </div>
        </details>
      </div>
    </div>
  );
};
